import { getLegalBranding } from './siteConfig.js';

export function getPrivacyPolicySections() {
  const b = getLegalBranding();
  const demoNote = b.isDemo
    ? ' This demonstration site uses fictional community data only. No real homeowner information is collected or stored.'
    : '';

  return {
    title: 'Privacy Policy',
    meta: `${b.communityName} Community Portal\nEffective date: ${b.effectiveDate}`,
    sections: [
      {
        heading: 'Introduction',
        body: [
          `This Privacy Policy describes how ${b.legalEntity} ("we," "us," or "our") handles information when you use our owner community website and mobile applications (together, the "Services"). The Services are intended for verified homeowners and authorized community members.${demoNote}`,
          'By using the Services, you agree to this Privacy Policy. If you do not agree, please do not use the Services.',
        ],
      },
      {
        heading: 'Information We Collect',
        subsections: [
          {
            title: 'Account and profile information',
            text: 'When you sign in, we collect information associated with your account, such as your email address and display name. Notification preferences you save in the app are stored with your account.',
          },
          {
            title: 'Homeowner and community records',
            text: 'The HOA maintains owner records such as lot information, contact details, invoices, and related community data. Authorized administrators manage this information for community operations.',
          },
          {
            title: 'Content you submit',
            text: 'If you use community features, we process content you choose to submit, such as message board posts, poll responses, photos, species identification entries, and similar community contributions.',
          },
          {
            title: 'Payment information',
            text: 'If you pay HOA invoices through the Services, payment card details are collected and processed by Stripe, our payment processor. We do not store full payment card numbers on our servers.',
          },
          {
            title: 'Gate access information',
            text: 'If you use gate access features, we process license plate numbers you register for automatic entry and keep access logs to operate and audit gate access for the community.',
          },
          {
            title: 'Device permissions',
            list: [
              'Camera and photo library — for community photo uploads and identification tools.',
              'Location — while using map-related features such as the neighborhood map or flight radar.',
              'Biometric sign-in (mobile) — stored locally on your device when enabled.',
            ],
          },
          {
            title: 'Technical information',
            text: 'We may process limited technical information needed to operate and protect the Services, such as request timestamps, IP addresses used for abuse prevention, and standard server logs.',
          },
        ],
      },
      {
        heading: 'How We Use Information',
        list: [
          'Provide, maintain, and secure the Services for community members',
          'Authenticate users and enforce access controls',
          'Display HOA documents, invoices, events, weather, and community tools',
          'Process payments through Stripe when you choose to pay online',
          'Operate gate access, license plate recognition, and related security logging',
          'Send notifications you have opted into',
          'Respond to support requests and protect against misuse or fraud',
        ],
      },
      {
        heading: 'How We Share Information',
        body: ['We do not sell your personal information. We may share information only as needed to operate the Services:'],
        list: [
          'Service providers — such as Google Firebase, Stripe, and other infrastructure providers.',
          'Community administrators — authorized HOA administrators managing accounts, invoices, and operations.',
          'Other community members — content you submit may be visible to other authenticated members per feature design.',
          'Legal and safety — when required by law or to protect rights, safety, or security.',
        ],
      },
      {
        heading: 'Data Retention',
        body: [
          'We retain information for as long as needed to provide the Services, meet HOA record-keeping needs, comply with legal obligations, and enforce agreements.',
        ],
      },
      {
        heading: 'Security',
        body: [
          'We use reasonable administrative, technical, and organizational safeguards. No method of transmission or storage is completely secure.',
        ],
      },
      {
        heading: 'Your Choices',
        list: [
          'Update profile and notification preferences in the app.',
          'Disable biometric sign-in on mobile at any time.',
          'Decline optional device permissions (some features may not work).',
          'Contact us for account or data access questions.',
        ],
      },
      {
        heading: "Children's Privacy",
        body: [
          'The Services are intended for adult homeowners and authorized community users, not children under 13.',
        ],
      },
      {
        heading: 'Changes to This Policy',
        body: [
          'We may update this Privacy Policy from time to time. Continued use after changes become effective means you accept the updated policy.',
        ],
      },
      {
        heading: 'Contact Us',
        contact: true,
      },
    ],
  };
}

export function getTermsSections() {
  const b = getLegalBranding();
  const demoClause = b.isDemo
    ? ' This is a demonstration environment. All community names, owners, financial figures, and operational data are fictional. Do not enter real personal or payment information.'
    : '';

  return {
    title: 'Terms and Conditions',
    meta: `${b.communityName} Community Portal\nEffective date: ${b.effectiveDate}`,
    sections: [
      {
        heading: 'Agreement',
        body: [
          `These Terms and Conditions ("Terms") govern your access to and use of the ${b.communityName} community portal website and mobile applications (the "Services"), operated for ${b.legalEntity}.${demoClause}`,
          'By accessing or using the Services, you agree to these Terms. If you do not agree, do not use the Services.',
        ],
      },
      {
        heading: 'Eligibility',
        body: [
          'The Services are intended for verified property owners, authorized residents, and HOA administrators. You must be at least 18 years old and authorized by the community to create an account.',
        ],
      },
      {
        heading: 'Accounts and access',
        list: [
          'You are responsible for safeguarding your login credentials.',
          'You must promptly notify administrators of unauthorized access.',
          'We may suspend or terminate access for violations of these Terms or community rules.',
          'Administrator accounts carry additional responsibilities for accurate records and lawful use of owner data.',
        ],
      },
      {
        heading: 'Acceptable use',
        list: [
          'Do not harass, defame, or post unlawful content on community features.',
          'Do not attempt to bypass security controls, scrape data, or disrupt the Services.',
          'Do not use gate, mailbox, or vehicle monitoring tools for surveillance outside community policies.',
          'Respect intellectual property and privacy of other members.',
        ],
      },
      {
        heading: 'Payments',
        body: [
          'Online invoice payments are processed by Stripe. By paying through the Services, you also agree to Stripe\'s terms. Assessment amounts, due dates, and disputes are governed by HOA governing documents.',
        ],
      },
      {
        heading: 'Community content',
        body: [
          'You retain ownership of content you submit, but grant the HOA a license to display and store it within the Services for community operations. Administrators may moderate or remove content that violates community standards or law.',
        ],
      },
      {
        heading: 'Third-party services',
        body: [
          'The Services integrate third-party providers (e.g., weather, mapping, payment, camera, and identification APIs). Those services are subject to their own terms and availability; we are not responsible for third-party outages.',
        ],
      },
      {
        heading: 'Disclaimer',
        body: [
          'THE SERVICES ARE PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. Identification tools, monitoring dashboards, and alerts are informational and may contain errors. Always verify safety-critical information independently.',
        ],
      },
      {
        heading: 'Limitation of liability',
        body: [
          'To the fullest extent permitted by law, we are not liable for indirect, incidental, or consequential damages arising from use of the Services. Our total liability for any claim shall not exceed the amount you paid for Services in the prior twelve months, or $100 if none.',
        ],
      },
      {
        heading: 'Changes',
        body: [
          'We may update these Terms from time to time. Material changes will be reflected by updating the effective date. Continued use constitutes acceptance.',
        ],
      },
      {
        heading: 'Contact',
        contact: true,
      },
    ],
  };
}